<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <div
        ref="autocomplete"
        v-clickoutside="{ handler: close, include: [suggestionsRef] }"
        class="el-autocomplete"
        aria-haspopup="listbox"
        role="combobox"
        :aria-expanded="suggestionVisible"
        :aria-owns="autocompleteId"
        :class="{'is-opened': suggestionVisible && !separateSuggestions}"
    >
        <el-input
            ref="input"
            v-bind="$props"
            :clearable="showClear"
            @input="handleChange"
            @focus="handleFocus"
            @blur="handleBlur"
            @keydown.up.stop.prevent="navigateOptions('prev')"
            @keydown.down.stop.prevent="navigateOptions('next')"
            @keydown.enter="handleKeyEnter"
            @keydown.tab="close"
            @keydown.esc.stop.prevent="close"
            @clear="handleClear"
        >
            <template v-if="$slots.prepend" #prepend>
                <slot name="prepend" />
            </template>
            <template v-if="$slots.append" #append>
                <slot name="append" />
            </template>
            <template v-if="$slots.prefix" #prefix>
                <slot name="prefix" />
            </template>
            <template #suffix>
                <slot name="suffix">
                    <el-spinner
                        v-if="loading && modelValue"
                    />
                </slot>
            </template>
        </el-input>

        <teleport to="#teleports">
            <el-autocomplete-suggestions
                v-if="!disableSuggestions"
                :id="autocompleteId"
                ref="suggestionsRef"
                :popper-class="popperClass"
                placement="bottom"
            >
                <li
                    v-for="(item, index) in suggestions"
                    :id="`${autocompleteId}-item-${index}`"
                    :key="index"
                    class="el-autocomplete-suggestion__item"
                    :class="{ highlighted: highlightedIndex === index }"
                    role="option"
                    :aria-selected="highlightedIndex === index"
                    @click="select(item)"
                >
                    <slot :item="item">
                        {{ item[valueKey] }}
                    </slot>
                </li>
                <div
                    v-if="!isValidData"
                    class="el-autocomplete-suggestion__no-data"
                >
                    <span class="el-text el-text--body-32 el-text--color-fixed-light">
                        {{ noDataMessage }}
                    </span>
                </div>
            </el-autocomplete-suggestions>
        </teleport>
    </div>
</template>

<script setup>
import { ref, computed, watch, provide, reactive, nextTick, onMounted } from 'vue';
import ElInput from '@core/components/input/input.vue';
import ElSpinner from '@core/components/spinner/spinner.vue';
import { useDebouncedFn } from '@core/composables/useDebouncedFn';
import { useDropdownScroll } from '@core/composables/useDropdownScroll.ts';
import { useKeyboardNavigate } from '@core/composables/useKeyboardNavigate.ts';
import Clickoutside from '@utils/v-clickoutside.ts';
import ElAutocompleteSuggestions from './autocomplete-suggestions.vue';

const emit = defineEmits(['input', 'select', 'clear', 'focus', 'blur', 'update:modelValue']);

const props = defineProps({
    valueKey: { type: String, default: 'value' },
    noDataMessage: { type: String, default: () => 'No data found' },
    popperClass: { type: [String, Object], default: '' },
    separateSuggestions: Boolean,
    placeholder: { type: String, default: () => 'Search' },
    disabled: Boolean,
    clearable: { type: Boolean, default: true },
    disableSuggestions: Boolean,
    embedded: Boolean,
    maxlength: { type: Number, default: null },
    name: { type: String, default: '' },
    size: { type: String, default: '' },
    modelValue: { type: String, default: '' },
    autofocus: Boolean,
    fetchSuggestions: { type: Function, default: null },
    triggerOnFocus: { type: Boolean, default: true },
    customItem: { type: String, default: '' },
    selectWhenUnmatched: { type: Boolean, default: true },
    prefixIcon: { type: String, default: '' },
    suffixIcon: { type: String, default: 'search' },
    label: { type: String, default: '' },
    id: { type: String, default: '' },
    debounceTimer: { type: Number, default: 500 },
});

defineOptions({
    directives: {
        clickoutside: Clickoutside,
    },
});

const suggestionsRef = ref(null);
const input = ref(null);
const autocomplete = ref(null);
const { debouncedFn: debouncedGetData } = useDebouncedFn(getData, props.debounceTimer);

const autocompleteId = ref(props.id || `el-autocomplete-${Math.floor(Math.random() * 10000)}`);
const activated = ref(false);
const suggestions = ref([]);
const loading = ref(false);
const isValidData = ref(false);

const showClear = computed(() => props.clearable && props.modelValue && !loading.value);
const suggestionVisible = computed(() => activated.value && !loading.value && props.modelValue && !props.disableSuggestions);

const options = computed(() =>
    suggestions.value.map((item) => ({
        value: item[props.valueKey],
        label: item[props.valueKey],
        visible: true,
        disabled: false,
        groupDisabled: false,
        isSelected: item[props.valueKey] === props.modelValue,
    })),
);

const { scrollToOption } = useDropdownScroll(suggestionsRef, 'el-autocomplete-suggestion__wrap');
const { hoverIndex: highlightedIndex, navigateOptions } = useKeyboardNavigate({
    options,
    visible: suggestionVisible,
    scrollToOption,
    selected: ref({ value: props.modelValue }),
});

const dropdown = reactive({
    get input() {
        return input.value;
    },
    visible: false,
    show(val) { dropdown.visible = val; },
    hide() { dropdown.visible = false; },
});

provide('autocomplete', dropdown);

defineExpose({ input });


onMounted(() => {
    const $input = autocomplete.value?.querySelector('.el-input__container');
    if ($input) {
        $input.setAttribute('role', 'textbox');
        $input.setAttribute('aria-autocomplete', 'list');
        $input.setAttribute('aria-controls', autocompleteId.value);
    }
});

watch(suggestions, (value) => {
    isValidData.value = Array.isArray(value) && value.length > 0;
});

watch(suggestionVisible, (val) => {
    dropdown.show(val);
});

watch(highlightedIndex, (val) => {
    if (val >= 0) scrollToOption(options.value[val]);
    const container = autocomplete.value?.querySelector('.el-input__container');
    if (container && val >= 0) {
        container.setAttribute('aria-activedescendant', `${autocompleteId.value}-item-${val}`);
    }
});
function handleChange(value) {
    loading.value = !props.disableSuggestions;
    emit('update:modelValue', value);
    emit('input', value);
    if (!props.triggerOnFocus && !value) {
        suggestions.value = [];
        return;
    }
    debouncedGetData(value);
    activated.value = true;
}

function handleFocus(event) {
    emit('focus', event);
}

function handleBlur(event) {
    emit('blur', event);
}

function handleClear() {
    emit('clear');
}

function getData(queryString) {
    highlightedIndex.value = -1;
    if (!queryString || props.disableSuggestions) {
        loading.value = false;
        return;
    }
    loading.value = true;
    if (!props.fetchSuggestions) {
        loading.value = false;
        suggestions.value = [];
    }
    props.fetchSuggestions?.(queryString, (res) => {
        loading.value = false;
        if (Array.isArray(res)) suggestions.value = res;
    });
}

function handleKeyEnter(e) {
    if (suggestionVisible.value && highlightedIndex.value >= 0 && highlightedIndex.value < suggestions.value.length) {
        dropdown.hide();
        e.preventDefault();
        select(suggestions.value[highlightedIndex.value]);
    } else if (props.selectWhenUnmatched) {
        emit('update:modelValue', props.modelValue);
        emit('select', props.modelValue);
        activated.value = false;
        nextTick(() => {
            suggestions.value = [];
        });
    }
}

function select(item) {
    emit('update:modelValue', item[props.valueKey]);
    emit('input', item[props.valueKey]);
    emit('select', item);
    nextTick(() => {
        dropdown.hide();
        close();
        suggestions.value = [];
    });
}

function close() {
    activated.value = false;
}
</script>

<style lang="postcss" scoped>
.el-autocomplete {
    :deep(.el-input) {
        &__container {
            height: 32px;
        }
        &__wrapper {
            padding-top: 3px;
        }
        &__editor {
            font-size: 14px;
        }
    }
    &-suggestion {
        position: absolute;
        z-index: 2100;
        background: var(--av-inversed-primary);
        border: 1px solid var(--av-brand-primary);
        box-shadow: var(--av-shadow-regular);
        color: var(--av-fixed-primary);
        border-radius: 4px;
        box-sizing: border-box;
        :deep(&__wrap) {
            margin: 8px 0;
        }
        :deep(&__item) {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            padding: 4px 16px;
            margin: 0;
            cursor: pointer;
            color: var(--av-fixed-primary);
            font-size: 14px;
            line-height: 32px;
            list-style: none;
            &:hover {
                background-color: var(--el-secondary-hover);
            }
            &.highlighted {
                color: var(--av-fixed-primary);
                font-weight: 400;
                background-color: var(--el-secondary-active);
            }
        }
        &__no-data {
            @mixin body;
            text-align: center;
            padding: 24px 16px;
                color: var(--av-fixed-light);
        }
    }
}
</style>
